import { Button } from "@sellar-io/components";
import { ReactElement, useState } from "react";
import { SpinnerLegacy } from "../../../components";
import { Alert } from "../../../components/alert/Alert";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../components/modal/Dialog";
import { Toast } from "../../../components/toast/Toast";
import { useToast } from "../../../components/toast/useToast.shared";
import { useOrderChargeRemainingOrderBalance } from "../../../hooks/api/v2/order/v2/useOrderChargeRemainingOrderBalance";
import { formatCurrency } from "@sellar-io/common";
import { useOrder } from "../../../hooks/api/v2/order/useOrder";

export function AdminChargeRemainingOrderBalance({
  active,
  orderId,
  onDismiss,
}: {
  active: boolean;
  orderId: number;
  onDismiss: () => void;
}): ReactElement {
  const chargeOrderRemainingBalanceMutation =
    useOrderChargeRemainingOrderBalance(orderId);
  const { toast } = useToast();
  const [transactionPending, setTransactionPending] = useState(false);

  // the dialog component instance persists between opens (it isn't
  // unmounted by the parent), so the mutation's error state must be reset
  // whenever the dialog closes - otherwise reopening it would immediately
  // show the previous attempt's failed screen
  const handleDismiss = () => {
    chargeOrderRemainingBalanceMutation.reset();
    onDismiss();
  };

  const confirm = () => {
    setTransactionPending(true);
    chargeOrderRemainingBalanceMutation.mutate(undefined, {
      onSuccess: () => {
        toast({
          placement: "bottom",
          render: () => (
            <Toast icon="CHECK">Remaining balance charged successfully.</Toast>
          ),
        });
        onDismiss();
      },
      onSettled: () => {
        setTransactionPending(false);
      },
    });
  };

  const orderQuery = useOrder(orderId);
  const order = orderQuery.data;

  const depositPayment =
    order?.PaymentList.find((payment) => payment.type === "cardDeposit") ??
    null;

  const depositPaidAmount = depositPayment?.amount ?? 0;
  const remainingBalance = (order?.price ?? 0) - depositPaidAmount;

  const hasFailed = chargeOrderRemainingBalanceMutation.isError;

  return (
    <Dialog
      active={active}
      onDismiss={transactionPending ? undefined : handleDismiss}
    >
      <DialogNavigation
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={transactionPending ? undefined : handleDismiss}
        title="Charge remaining order balance"
      />
      <DialogContent>
        {hasFailed ? (
          <Alert
            type={"error"}
            message={
              "Payment failed. The customer has been notified and can retry with a new card."
            }
          />
        ) : transactionPending ? (
          <div className="flex flex-col items-center justify-between gap-2">
            <SpinnerLegacy />
            Please do not navigate away until the charge transaction is
            complete.
          </div>
        ) : (
          <div>
            Please confirm you wish to charge the buyer's card the remaining
            order balance of{" "}
            <span className="font-semibold">
              {formatCurrency(remainingBalance)}
            </span>
          </div>
        )}
      </DialogContent>
      <DialogCTA>
        {hasFailed ? (
          <Button onClick={handleDismiss}>Close</Button>
        ) : (
          !transactionPending && <Button onClick={confirm}>Confirm</Button>
        )}
      </DialogCTA>
    </Dialog>
  );
}
